import { Component, Input, Inject, NgZone, OnDestroy, PLATFORM_ID, ChangeDetectorRef, forwardRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { DashboardBaseChart } from '../shared/dashboard-base-chart';
import { DashboardBaseComponent } from '../shared/dashboard-base-component';
import {ChartLoadingComponent} from "../chart-loading/chart-loading.component";

export interface ChartDonutSlice {
  /** Legend / tooltip label for this slice */
  label: string;
  /** Numeric value — used to compute the slice size and legend percentage */
  value: number;
  /** Hex fill color for the slice */
  color: string;
}

export interface ChartDonutLegendConfig {
  fontSize?: number;
  fontWeight?: '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900' | 'normal' | 'bold' | 'bolder' | 'lighter';
}

export interface ChartDonutConfig {
  /** Unique DOM id for the amCharts root container */
  chartId: string;
  /** Large text rendered in the donut hole (e.g. total amount). Optional — omit to hide */
  centerLabel?: string;
  /** Smaller text below centerLabel (e.g. 'Total'). Optional */
  centerSub?: string;
  /** Slice definitions — determines pie segments, colors, and legend entries */
  slices: ChartDonutSlice[];
  /** amCharts5 tooltip format string. e.g. "{category}: {value}" or "{category}: {formattedValue}" */
  tooltipText?: string;
  /** When set, adds a `formattedValue` field to each slice — use with tooltipText: '{category}: {formattedValue}' */
  tooltipValueFormatter?: (v: number) => string;
  /** Optional legend label styling */
  legend?: ChartDonutLegendConfig;
}

export interface ChartDonutCardConfig {
  title: string;
  sub?: string;
  lastUpdated?: string;
  chart: ChartDonutConfig;
}

@Component({
  selector: 'app-chart-donut',
  templateUrl: './chart-donut.component.html',
  styleUrls: ['./chart-donut.component.scss'],
  standalone: true,
  imports: [
    ChartLoadingComponent
  ],
  providers: [
    {
      provide: DashboardBaseComponent,
      useExisting: forwardRef(() => ChartDonutComponent)
    }
  ]
})
export class ChartDonutComponent extends DashboardBaseChart implements OnDestroy {
  @Input() config!: ChartDonutConfig;

  isLoading = true;

  private series?: am5percent.PieSeries;
  private centerLabelObj?: am5.Label;
  private legend?: am5.Legend;
  private resizeObserver?: ResizeObserver;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  protected initChart(): void {
    if (!isPlatformBrowser(this.platformId) || !this.config?.chartId) return;

    this.zone.runOutsideAngular(() => {
      const root = am5.Root.new(this.config.chartId);
      root.setThemes([am5themes_Animated.new(root)]);
      if (root._logo) root._logo.dispose();
      this.root = root;

      const textColor  = am5.color(0xd9e8ff);
      const mutedColor = am5.color(0x9db8d7);

      const wrapper = root.container.children.push(
        am5.Container.new(root, {
          width: am5.percent(100),
          height: am5.percent(100),
          layout: root.horizontalLayout,
        })
      );

      const chart = wrapper.children.push(
        am5percent.PieChart.new(root, {
          innerRadius: am5.percent(60),
          paddingTop: 6,
          paddingBottom: 6,
        })
      );

      this.series = chart.series.push(
        am5percent.PieSeries.new(root, {
          valueField: 'value',
          categoryField: 'label',
          alignLabels: false,
        })
      );

      this.series.slices.template.setAll({
        templateField: 'settings',
        strokeOpacity: 0,
        cornerRadius: 2,
        shadowOpacity: 0.1,
        shadowOffsetX: 2,
        shadowOffsetY: 2,
        shadowColor: am5.color(0x000000),
        shadowBlur: 10,
      });
      this.series.slices.template.set('fillGradient', am5.RadialGradient.new(root, {
        stops: [
          { brighten: 0 },
          { brighten: -0.5 },
          { brighten: -0.3 },
          { brighten: 0.5 },
          { brighten: -0.3 },
        ],
      }));
      this.series.labels.template.setAll({ forceHidden: true });
      this.series.ticks.template.setAll({ forceHidden: true });
      if (this.config.tooltipText) {
        this.series.slices.template.set('tooltipText', this.config.tooltipText);
      }

      this.centerLabelObj = chart.seriesContainer.children.push(
        am5.Label.new(root, {
          text: this.buildCenterText(),
          textAlign: 'center',
          centerX: am5.percent(50),
          centerY: am5.percent(50),
          fill: textColor,
          populateText: true,
        })
      );

      this.legend = wrapper.children.push(
        am5.Legend.new(root, {
          centerY: am5.percent(50),
          y: am5.percent(50),
          layout: root.verticalLayout,
          marginLeft: 16,
          height: am5.percent(100),
          verticalScrollbar: am5.Scrollbar.new(root, { orientation: 'vertical' }),
        })
      );
      const legendFontSize   = this.config.legend?.fontSize   ?? 12;
      const legendFontWeight = this.config.legend?.fontWeight ?? '700';
      this.legend.labels.template.setAll({ fill: textColor, fontSize: legendFontSize, fontWeight: legendFontWeight });
      this.legend.valueLabels.template.setAll({ fill: mutedColor, fontSize: legendFontSize, fontWeight: legendFontWeight });

      // Responsive: move legend below on narrow screens
      const el = document.getElementById(this.config.chartId);
      if (el) {
        this.resizeObserver = new ResizeObserver((entries) => {
          const width = entries[0]?.contentRect.width ?? 0;
          if (width === 0) return;
          if (!this.legend)return;
          if (width <= 500) {
            el.style.height = '380px';
            wrapper.set('layout', root.verticalLayout);
            chart.setAll({ height: am5.percent(65) });
            this.legend.setAll({
              x: am5.percent(50), centerX: am5.percent(50),
              y: undefined, centerY: undefined,
              height: am5.percent(35),
              marginLeft: 0, layout: root.gridLayout,
            });
          } else {
            el.style.height = '245px';
            wrapper.set('layout', root.horizontalLayout);
            chart.setAll({ height: undefined });
            this.legend.setAll({
              x: undefined, centerX: undefined,
              y: am5.percent(50), centerY: am5.percent(50),
              height: am5.percent(100),
              marginLeft: 16, layout: root.verticalLayout,
            });
          }
        });
        this.resizeObserver.observe(el);
      }

      this.series.appear(500, 100);
    });
  }

  load(slices: ChartDonutSlice[]): void {
    this.isLoading = false;
    this.cdr.detectChanges();
    if (!this.root) this.initChart();
    if (!this.series) return;
    this.config.slices = slices;
    const fmt = this.config.tooltipValueFormatter;
    this.series?.data.setAll(
      slices.map(s => ({
        ...s,
        settings: { fill: am5.color(s.color) },
        ...(fmt ? { formattedValue: fmt(s.value) } : {}),
      }))
    );
    this.legend?.data.setAll(this.series.dataItems);
    this.centerLabelObj?.set('text', this.buildCenterText());
    this.isLoaded = true;
  }

  private buildCenterText(): string {
    return [
      this.config.centerLabel ? `[fontSize:20px fontWeight:800]${this.config.centerLabel}[/]` : '',
      this.config.centerSub  ? `[fontSize:12px]${this.config.centerSub}[/]` : '',
    ].filter(Boolean).join('\n');
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.root?.dispose();
  }

}
